import { useCallback, useEffect, useLayoutEffect, useRef, useState, type CSSProperties, type HTMLAttributes, type ReactNode, type Ref, type RefObject } from 'react';
import { createPortal } from 'react-dom';

export type Placement = 'bottom-start' | 'bottom-end' | 'top-start' | 'top-end' | 'bottom' | 'top' | 'right-start';

/**
 * Overlays are portalled so they are never clipped by `overflow` containers. When the anchor sits inside
 * a modal <dialog> (which lives in the browser top layer) the portal target is that dialog, otherwise
 * the overlay would render underneath it.
 */
export function Portal({ anchorRef, children }: { anchorRef?: RefObject<HTMLElement | null>; children: ReactNode }) {
  const [target, setTarget] = useState<HTMLElement | null>(null);
  useLayoutEffect(() => {
    setTarget((anchorRef?.current?.closest('dialog') as HTMLElement | null) ?? document.body);
  }, [anchorRef]);
  return target ? createPortal(children, target) : null;
}

export interface FloatingProps extends Omit<HTMLAttributes<HTMLDivElement>, 'style'> {
  anchorRef: RefObject<HTMLElement | null>;
  placement?: Placement;
  offset?: number;
  matchWidth?: boolean;
  zIndex?: number;
  /** Receives the floating element (for dismiss/focus management in the owner). */
  ref?: Ref<HTMLDivElement>;
  children: ReactNode;
}

function FloatingPanel({ anchorRef, placement, offset, matchWidth, zIndex = 70, ref, children, ...rest }: FloatingProps) {
  const local = useRef<HTMLDivElement | null>(null);
  // The hook lives HERE, inside the portal, so its layout effect runs once the element actually exists.
  const style = useFloatingPosition({ anchorRef, floatingRef: local, open: true, placement, offset, matchWidth });
  // Stable identity: React re-invokes a changed callback ref (null, then node) on every render.
  const setRef = useCallback(
    (node: HTMLDivElement | null) => {
      local.current = node;
      if (typeof ref === 'function') {
        ref(node);
      } else if (ref) {
        ref.current = node;
      }
    },
    [ref],
  );
  return (
    <div {...rest} ref={setRef} style={{ ...style, zIndex }}>
      {children}
    </div>
  );
}

/** A positioned, portalled panel anchored to `anchorRef` (menus, popovers, tooltips, listboxes). */
export function Floating(props: FloatingProps) {
  return (
    <Portal anchorRef={props.anchorRef}>
      <FloatingPanel {...props} />
    </Portal>
  );
}

interface PositionOptions {
  anchorRef: RefObject<HTMLElement | null>;
  floatingRef: RefObject<HTMLElement | null>;
  open: boolean;
  placement?: Placement;
  offset?: number;
  /** Make the floating element at least as wide as the anchor. */
  matchWidth?: boolean;
}

const VIEWPORT_PAD = 8;

/** Computes fixed-position coordinates next to an anchor, flipping to the other side when there is no room. */
export function useFloatingPosition({ anchorRef, floatingRef, open, placement = 'bottom-start', offset = 6, matchWidth }: PositionOptions): CSSProperties {
  const [style, setStyle] = useState<CSSProperties>({ position: 'fixed', top: 0, left: 0, visibility: 'hidden' });

  const update = useCallback(() => {
    const anchor = anchorRef.current;
    const floating = floatingRef.current;
    if (!anchor || !floating) {
      return;
    }
    const a = anchor.getBoundingClientRect();
    const f = floating.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const wantsTop = placement.startsWith('top');
    const spaceBelow = vh - a.bottom - offset - VIEWPORT_PAD;
    const spaceAbove = a.top - offset - VIEWPORT_PAD;
    const placeTop = wantsTop ? spaceAbove >= f.height || spaceAbove >= spaceBelow : spaceBelow < f.height && spaceAbove > spaceBelow;
    let top = placeTop ? a.top - f.height - offset : a.bottom + offset;
    let left: number;
    if (placement === 'right-start') {
      left = a.right + offset;
      top = a.top;
    } else if (placement.endsWith('end')) {
      left = a.right - f.width;
    } else if (placement === 'bottom' || placement === 'top') {
      left = a.left + a.width / 2 - f.width / 2;
    } else {
      left = a.left;
    }
    left = Math.max(VIEWPORT_PAD, Math.min(left, vw - f.width - VIEWPORT_PAD));
    top = Math.max(VIEWPORT_PAD, Math.min(top, vh - f.height - VIEWPORT_PAD));
    const next: CSSProperties = { position: 'fixed', top, left, minWidth: matchWidth ? a.width : undefined, visibility: 'visible' };
    // Keep the previous object when nothing moved, otherwise the every-render layout effect would loop.
    setStyle((prev) => (prev.top === next.top && prev.left === next.left && prev.minWidth === next.minWidth && prev.visibility === next.visibility ? prev : next));
  }, [anchorRef, floatingRef, placement, offset, matchWidth]);

  useLayoutEffect(() => {
    if (open) {
      update();
    }
  });

  useEffect(() => {
    if (!open) {
      return;
    }
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);
    return () => {
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
    };
  }, [open, update]);

  return style;
}

/** Calls `onDismiss` for an outside pointer press or Escape while `active`. */
export function useDismiss(active: boolean, refs: RefObject<HTMLElement | null>[], onDismiss: () => void): void {
  useEffect(() => {
    if (!active) {
      return;
    }
    const onPointer = (event: PointerEvent): void => {
      const target = event.target as Node;
      if (refs.some((ref) => ref.current?.contains(target))) {
        return;
      }
      onDismiss();
    };
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        onDismiss();
      }
    };
    document.addEventListener('pointerdown', onPointer, true);
    document.addEventListener('keydown', onKey, true);
    return () => {
      document.removeEventListener('pointerdown', onPointer, true);
      document.removeEventListener('keydown', onKey, true);
    };
  }, [active, refs, onDismiss]);
}
